/* eslint-disable @typescript-eslint/naming-convention */
/**
 * Main doc: https://dropbox.atlassian.net/wiki/spaces/_/pages/990052804
 */
import { DeviceType, SessionEventType, SourceContext } from 'js/file_viewer/core/logging/constants';
import { getWhitelistedFileExtension } from 'js/file_viewer/core/utils/extension_constants';
import { v4 } from 'js/file_viewer/core/utils/uuid';
/**
 * Create a new file preview session
 *
 * @export
 * @param {CreateFilePreviewSessionOpts} opts options to create previews session
 * @returns {FilePreviewSession}
 */
export function createFilePreviewSession(opts) {
    const { file_viewer_session_id = v4(), file_preview_session_id = v4(), file_id = '', sj_id, ns_id, file_extension = getWhitelistedFileExtension(''), device_type = DeviceType.desktop, extra = {}, file_preview_size, } = opts || {};
    return {
        file_viewer_session_id: file_viewer_session_id,
        file_preview_session_id,
        file_id,
        sj_id,
        ns_id,
        file_extension,
        device_type,
        file_preview_size,
        extra: {
            ...extra,
            source_context: extra.source_context || SourceContext.FileViewer,
            was_flipped: extra.was_flipped || false,
            file_preview_timeline: {},
        },
    };
}
/**
 * Mark event ts in session, also enforce event order
 * @param session session to mark
 * @param event Event
 */
export function recordEvent(session, event) {
    const { extra: { last_event: lastEvent }, } = session;
    /**
     * Don't double log
     */
    if (lastEvent === event) {
        return undefined;
    }
    /**
     * The order is roughly
     * 1. preview_attempted
     * 2. support_confirmed || support_denied
     * 3. support_confirmed -> download_attempted
     * 4. download_succeeded || download_failed
     * 5. download_succeeded -> render_succeeded || render_failed
     */
    switch (event) {
        case SessionEventType.FilePreviewSupportConfirmed:
        case SessionEventType.FilePreviewSupportDenied:
            if (lastEvent !== SessionEventType.FilePreviewAttemptStarted) {
                return undefined;
            }
            break;
        case SessionEventType.FilePreviewDownloadAttempted:
            if (lastEvent !== SessionEventType.FilePreviewSupportConfirmed) {
                return undefined;
            }
            break;
        case SessionEventType.FilePreviewDownloadFailed:
        case SessionEventType.FilePreviewDownloadSucceeded:
            if (lastEvent !== SessionEventType.FilePreviewDownloadAttempted) {
                return undefined;
            }
            break;
        case SessionEventType.FilePreviewRenderSucceeded:
            if (lastEvent !== SessionEventType.FilePreviewDownloadSucceeded) {
                return undefined;
            }
            break;
        case SessionEventType.FilePreviewRenderFailed:
            if (lastEvent !== SessionEventType.FilePreviewDownloadSucceeded) {
                return undefined;
            }
            break;
    }
    return {
        ...session,
        extra: {
            ...session.extra,
            file_preview_timeline: {
                ...session.extra.file_preview_timeline,
                [event]: Date.now(),
            },
        },
    };
}
export function updateSessionLastEvent(session, event) {
    return {
        ...session,
        extra: {
            ...session.extra,
            last_event: event,
        },
    };
}
// We keep the full logging session outside the store to prevent unnecessary
// re-renders due to update the logging session state.  Specifically, the
// session gets updated by calls to `logSessionEvent` which is called in
// a `useMemo` and then may lead to re-render attempts in code which
// depends on the session id which React complains about.
let sessionIdToSession = {};
export const initializeLoggingSession = (sessionId, options) => {
    sessionIdToSession = {
        ...sessionIdToSession,
        [sessionId]: createFilePreviewSession(options),
    };
};
export const setLoggingSession = (sessionId, session) => {
    sessionIdToSession = {
        ...sessionIdToSession,
        [sessionId]: session,
    };
};
export const getLoggingSession = (sessionId) => sessionId !== undefined ? sessionIdToSession[sessionId] : undefined;
/**
 * Serialize extra databag to a Hive-safe format (which is string->string mapping).
 * The reason is that our log receiver in Python serializes primitive data types
 * differently (e.g boolean -> True/False) so normalizing to string makes
 * filtering easier.
 * @param extra Extra databag
 */
function serializeExtra(extra) {
    return Object.keys(extra).reduce((all, k) => {
        const val = extra[k];
        if (typeof val === 'string' || typeof val === 'number') {
            all[k] = String(val);
        }
        else if (typeof val === 'boolean') {
            all[k] = String(+val);
        }
        else if (typeof val === 'object' && val) {
            all[k] = JSON.stringify(val);
        }
        return all;
    }, {});
}
/**
 * Serialize a session into logger-friendly format.
 * This primarily serializes extra
 * @param session Session object
 */
export function serializeSession(session, extra) {
    return {
        ...session,
        file_extension: getWhitelistedFileExtension(session.file_extension || ''),
        extra: serializeExtra({ ...session.extra, ...extra }),
    };
}
//# sourceMappingURL=session.preenvesnextjs.map
